import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactPlayer from 'react-player';
import { CaptionsCue, PlayerBar } from '@praktio/common-ui';
import { Utils } from '@praktio/common';
import { ICaptionsData } from '@praktio/common/lib/typings';

import { isEmpty } from '../../../utils/functions';
import { clear, updatePlayer } from '../../../ducks/explainerReducer';
import ExplainerPanels from './ExplainerPanels';
import { update as updateSlide, markViewed } from '../../../ducks/slidesLitmosReducer';
import { RootState } from '../../../ducks/configureStore';
import { CSSTransition } from 'react-transition-group';

import './Explainer.scss';
import { ExplainerBar } from './ExplainerBar';
import classNames from 'classnames';
import { WindowSizes } from '../../../utils/constants';

const { parseVTT } = Utils.Captions;

interface Props {
  disableAutoPlay?: boolean;
  windowSize: WindowSizes;
  hasLandscapeDoc: boolean;
  hasPortraitDoc: boolean;
}

const PlayerBarHideInterval = 1000 * 2; // Two seconds;

const Explainer: FC<Props> = ({ disableAutoPlay, windowSize, hasLandscapeDoc, hasPortraitDoc }) => {
  const dispatch = useDispatch();
  const {
    playing,
    seeking,
    volume,
    playbackRate,
    autoAdvance,
    showCaptions,
    playedSeconds,
    slideUnlocked,
    duration,
    slide,
    showCollapsiblePanel,
    numberOfPanels,
  } = useSelector((state: RootState) => {
    const { player } = state.explanation;
    const { settings, currentSlide } = state.slidesLitmos;
    const { panels } = currentSlide || {};
    const { learnerId } = state.learner;
    const { showCollapsiblePanel } = state.quizLitmos;
    const {
      playing,
      seeking,
      volume,
      playbackRate,
      autoAdvance,
      showCaptions,
      playedSeconds,
      showPlayTooltip,
      duration,
    } = player;

    const { unlocked } = (currentSlide || {}).learnerData || {};
    const slide = currentSlide || ({} as Slide);
    const numberOfPanels = panels?.length;
    return {
      playing,
      seeking,
      volume,
      playbackRate: !unlocked && playbackRate && playbackRate > 1 ? 1 : playbackRate,
      autoAdvance,
      showCaptions,
      playedSeconds: playedSeconds || 0,
      slideUnlocked: !!unlocked,
      learnerId,
      settings,
      showPlayTooltip,
      showCollapsiblePanel,
      numberOfPanels,
      // We need to retrieve the duration first from props (which pulls it from ReactPlayer,
      // which pulls it from an HTML audio element) because that has the value that
      // the Player uses for determining when to end playing. The slide's duration is calculated
      // using a separate NPM module when the audio is first uploaded, but the two
      // values don't always align and we can end up in a situation where the audio
      // finishes but the progress bar doesn't show it. This gurarantees the progress
      // bar to always stop at the end of the audio.
      duration: duration || slide.duration || 0,
      slide,
    };
  });

  const { audio, timeline } = slide;
  const { captions } = audio || {};

  const playerBarRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<ReactPlayer>();
  const timerId = useRef<NodeJS.Timeout>();
  const prevSlideId = useRef<string | undefined>();
  const prevPlayedSeconds = useRef(0);
  const [flashIcon, setFlashIcon] = useState('');
  const [showFlashIcon, setShowFlashIcon] = useState(false);
  const [showPlayerBar, setShowPlayerBar] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [playerBarVisible, setPlayerBarVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [processedCaptions, setProcessedCaptions] = useState<ICaptionsData[]>(parseVTT(captions));

  const findActiveAnnotations = useCallback(() => {
    const { timeline } = slide;
    // For some reason, playedSeconds is sometimes greater
    // than the duration (something going on in ReactPlayer).
    // If that is the case, set to duration.
    let seconds: number =
      (!isEmpty(duration) && playedSeconds > duration ? duration : playedSeconds) || 0;
    const newTimeline = timeline.map((item) => {
      const start = item.range ? parseFloat(item.range[0]) : 0;
      const end =
        (item.ignoreEndDuration ? duration : item.range ? parseFloat(item.range[1]) : 0) || 0;
      const threshold = 0.001;
      const endWithinThreshold = Math.abs(end - (duration || 0)) <= threshold;
      return {
        ...item,
        active:
          start <= seconds && (item.ignoreEndDuration || end >= seconds || endWithinThreshold),
        pastEnd: !item.ignoreEndDuration && seconds > end,
      };
    });

    dispatch(
      updateSlide({
        currentSlide: { ...slide, timeline: newTimeline },
      }),
    );
  }, [slide, playedSeconds, duration, dispatch]);

  const showPlayerAndResetTimer = () => {
    setShowPlayerBar(true);
    if (timerId.current) {
      clearTimeout(timerId.current);
    }

    timerId.current = setTimeout(() => setShowPlayerBar(false), PlayerBarHideInterval);
  };

  const handleMouseMove = () => {
    showPlayerAndResetTimer();
  };

  const handleWindowKeyDown = (event: KeyboardEvent) => {
    showPlayerAndResetTimer();
    if (event.code === 'Space') {
      event.stopPropagation();
      event.preventDefault();
      dispatch(updatePlayer({ playing: !playing }));
      setFlashIcon(playing ? 'pause' : 'play');
      setShowFlashIcon(true);
    }
  };

  const handlePlayerBarKeyDown = (event: KeyboardEvent) => {
    if (event.code === 'Space') {
      event.stopPropagation();
    }
  };

  useEffect(() => {
    if (slide && slide.timeline) {
      findActiveAnnotations();
    }

    if (timerId.current) {
      clearTimeout(timerId.current);
    }

    timerId.current = setTimeout(() => setShowPlayerBar(false), PlayerBarHideInterval);

    setShowPlayerBar(true);
    setPlayerBarVisible(true);
    return () => {
      dispatch(clear());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('keydown', handleWindowKeyDown);

    const pbRef = playerBarRef.current;
    if (pbRef) {
      pbRef.addEventListener('keydown', handlePlayerBarKeyDown);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('keydown', handleWindowKeyDown);

      if (pbRef) {
        pbRef.removeEventListener('keydown', handlePlayerBarKeyDown);
      }
    };
  });

  useEffect(() => {
    if (slide?._id !== prevSlideId.current) {
      dispatch(clear());
      findActiveAnnotations();
    } else if (prevPlayedSeconds.current !== playedSeconds && prevSlideId.current === slide?._id) {
      findActiveAnnotations();
    }

    prevSlideId.current = slide?._id;
    prevPlayedSeconds.current = playedSeconds;
  }, [slide, playedSeconds, findActiveAnnotations, dispatch]);

  useEffect(() => {
    if (captions) {
      setProcessedCaptions(parseVTT(captions));
    }
  }, [captions]);

  const refCallback = (player: ReactPlayer) => {
    playerRef.current = player;
  };

  const onPlayerReady = () => {
    setLoaded(true);
  };

  const onPlayerPlay = () => {
    dispatch(
      updatePlayer({
        playing: true,
        seeking: false,
      }),
    );
  };

  const onPlayerProgress = (state: { playedSeconds: number }) => {
    // We only want to update time progress bar if we are not currently seeking
    if (!seeking) {
      dispatch(updatePlayer({ playedSeconds: state.playedSeconds }));
    }
  };

  const onPlayerEnded = () => {
    dispatch(markViewed());
  };

  const onPlayerError = (err: Error) => {
    console.error(err);
  };

  const onPlayerDuration = (duration: number) => {
    dispatch(updatePlayer({ duration }));
  };

  const onPlaying = (value: boolean) => {
    dispatch(
      updatePlayer({
        playing: value,
      }),
    );

    setFlashIcon(!value ? 'pause' : 'play');
    setShowFlashIcon(true);
  };

  const onSeeking = (value: boolean) => {
    dispatch(
      updatePlayer({
        playing: !value,
      }),
    );
  };

  const onSeekTo = (value: number, isReplay?: boolean) => {
    playerRef.current?.seekTo(value);
    dispatch(
      updatePlayer({
        playedSeconds: value,
      }),
    );

    if (isReplay) {
      setFlashIcon('rewind');
      setShowFlashIcon(true);
    }
  };

  const updateAutoAdvance = (value: boolean) => {
    localStorage.setItem('autoAdvance', value.toString());
    dispatch(
      updatePlayer({
        autoAdvance: value,
      }),
    );
  };

  const updatePlaybackRate = (value: number) => {
    let rate = value;
    if (!slideUnlocked && (playbackRate || 0) > 1) {
      rate = 1;
    }

    localStorage.setItem('playbackRate', rate.toString());
    dispatch(
      updatePlayer({
        playbackRate: rate,
      }),
    );
  };

  const updateShowCaptions = () => {
    const value = !showCaptions;
    localStorage.setItem('showCaptions', value.toString());
    dispatch(
      updatePlayer({
        showCaptions: value,
      }),
    );
  };

  const renderFlashIcon = () => {
    let icon = '';
    switch (flashIcon) {
      case 'play':
        icon = 'play_circle';
        break;
      case 'pause':
        icon = 'pause_circle';
        break;
      case 'rewind':
        icon = 'replay_10';
        break;
    }

    return (
      <CSSTransition
        in={showFlashIcon}
        timeout={300}
        classNames="flash-icon-animation"
        unmountOnExit={true}
        onEntered={() => {
          setShowFlashIcon(false);
        }}
      >
        <span className="material-icons flash-icon">{icon}</span>
      </CSSTransition>
    );
  };

  const hasCollapsiblePanel = !!(numberOfPanels && numberOfPanels > 1);
  let audioUrl = undefined;
  if (audio) {
    audioUrl = audio.url;
  }

  const config = {
    file: {
      attributes: {
        crossOrigin: 'true',
        controls: false,
        autoPlay: disableAutoPlay ? false : true,
      },
    },
    forceVideo: true,
  };

  let currentCaptions: string[] = [];
  if (showCaptions && processedCaptions) {
    // Work backwards to find the captions for the current time
    for (let i = processedCaptions.length - 1; i >= 0; i -= 1) {
      const data = processedCaptions[i];
      // Multiply by a 1000 because the data is in milliseconds
      if (playedSeconds * 1000 >= (data.start || 0)) {
        currentCaptions = data.captions;
        break;
      }
    }
  }

  return (
    <div
      className={classNames('Explainer', {
        'has-collapsible-panel': hasCollapsiblePanel,
        'collapsible-panel-expanded': hasCollapsiblePanel && showCollapsiblePanel,
        'has-landscape-doc': hasLandscapeDoc,
        'has-portrait-doc': hasPortraitDoc,
      })}
    >
      <div className={classNames('flash-icon-holder', { 'to-front': showFlashIcon })}>
        {/* PLAYER ICON FLASH */}
        {renderFlashIcon()}
      </div>
      {audio !== null && (
        <div className="player-area">
          <div className="player-area-container">
            <ReactPlayer
              ref={refCallback}
              config={config}
              className="react-player"
              width="100%"
              height="43px"
              url={audioUrl}
              playing={loaded ? !seeking && playing : false}
              playbackRate={loaded ? playbackRate : 1}
              volume={loaded ? volume : undefined}
              progressInterval={25}
              onReady={onPlayerReady}
              onPlay={onPlayerPlay}
              onProgress={onPlayerProgress}
              onEnded={() => {
                if (timerId.current) {
                  clearTimeout(timerId.current);
                }
                setShowPlayerBar(true);
                onPlayerEnded();
              }}
              onError={onPlayerError}
              onDuration={onPlayerDuration}
            />
          </div>

          {/* PLAYER BAR */}
          <CSSTransition
            in={showPlayerBar || !playing || settingsOpen}
            timeout={300}
            classNames="player-bar-animation"
            // These pieces of state are 'self-owned' by the player bar
            // in order to determine when it can display tooltips.
            onEntered={() => setPlayerBarVisible(true)}
            onExited={() => setPlayerBarVisible(false)}
          >
            <PlayerBar
              playing={playing}
              unlocked={slideUnlocked}
              duration={duration}
              playedSeconds={playedSeconds}
              playbackRate={playbackRate}
              autoAdvance={autoAdvance}
              showCaptions={showCaptions}
              onSeeking={onSeeking}
              onSeekTo={onSeekTo}
              onPlaying={onPlaying}
              updatePlaybackRate={updatePlaybackRate}
              updateAutoAdvance={updateAutoAdvance}
              updateShowCaptions={updateShowCaptions}
              setSettingsOpen={setSettingsOpen}
              showPlayerAndResetTimer={showPlayerAndResetTimer}
              autoHideable
              isBeingShown={playerBarVisible}
              ref={playerBarRef}
            />
          </CSSTransition>
        </div>
      )}
      {/* PANELS */}
      <div className="lower-content">
        <ExplainerBar
          text={slide.name}
          showCollapsiblePanel={!!showCollapsiblePanel}
          hasCollapsiblePanel={hasCollapsiblePanel}
        />
        <div className="ExplainerPanels-wrapper">
          {timeline.length > 0 && (
            <ExplainerPanels
              windowSize={windowSize}
              hasLandscapeDoc={hasLandscapeDoc}
              hasPortraitDoc={hasPortraitDoc}
            />
          )}
        </div>
      </div>
      <CaptionsCue
        playerBarActive={showPlayerBar || !playing || settingsOpen}
        lines={currentCaptions}
      />
    </div>
  );
};

export default Explainer;
